import { useBreakpoint } from '@palmetto/palmetto-components';
import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { RichTextNode } from 'types';

import { CHECKLIST_DRAWER_WIDTH, CHECKLIST_DRAWER_Z_INDEX, NAV_HEIGHT } from './checklistLayout';
import { ChecklistTooltipPopover } from './ChecklistTooltipPopover';

const DOCK_GAP = 8;

interface ActiveTooltip {
  id: string;
  content: RichTextNode[];
  mode: string;
}

interface ChecklistTooltipContextValue {
  activeId: string | null;
  openTooltip: (id: string, content: RichTextNode[]) => void;
  closeTooltip: () => void;
}

const noop = () => {};

const ChecklistTooltipContext = createContext<ChecklistTooltipContextValue>({
  activeId: null,
  openTooltip: noop,
  closeTooltip: noop,
});

export const useChecklistTooltip = () => useContext(ChecklistTooltipContext);

const ChecklistTooltipDock: React.FC<{ tooltip: ActiveTooltip; onClose: () => void }> = ({ tooltip, onClose }) => {
  const { isPhone } = useBreakpoint();

  const style = useMemo<React.CSSProperties>(
    () => ({
      position: 'fixed',
      zIndex: CHECKLIST_DRAWER_Z_INDEX + 1,
      maxHeight: `calc(100vh - ${NAV_HEIGHT + DOCK_GAP * 2}px)`,
      overflowY: 'auto',
      ...(isPhone
        ? { left: DOCK_GAP, right: DOCK_GAP, bottom: DOCK_GAP }
        : {
            left: CHECKLIST_DRAWER_WIDTH + DOCK_GAP,
            top: `calc(${NAV_HEIGHT}px + (100vh - ${NAV_HEIGHT}px) / 2)`,
            transform: 'translateY(-50%)',
          }),
    }),
    [isPhone],
  );

  return createPortal(
    <div data-mode={tooltip.mode}>
      <div data-theme="core" style={style}>
        <ChecklistTooltipPopover content={tooltip.content} onClose={onClose} />
      </div>
    </div>,
    document.body,
  );
};

export const ChecklistTooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tooltip, setTooltip] = useState<ActiveTooltip | null>(null);
  const sentinelRef = useRef<HTMLSpanElement>(null);

  const openTooltip = useCallback((id: string, content: RichTextNode[]) => {
    const mode =
      (sentinelRef.current?.closest('[data-mode]') ?? document.documentElement).getAttribute('data-mode') ?? 'light';
    setTooltip({ id, content, mode });
  }, []);
  const closeTooltip = useCallback(() => setTooltip(null), []);

  const value = useMemo(
    () => ({ activeId: tooltip?.id ?? null, openTooltip, closeTooltip }),
    [tooltip?.id, openTooltip, closeTooltip],
  );

  return (
    <ChecklistTooltipContext.Provider value={value}>
      <span ref={sentinelRef} hidden />
      {children}
      {tooltip && <ChecklistTooltipDock tooltip={tooltip} onClose={closeTooltip} />}
    </ChecklistTooltipContext.Provider>
  );
};
